import React, { useState, useEffect, useCallback } from "react";
import Select from "react-select";
import { useSelector } from "react-redux";

const HDPerformanceDashboard = () => {
  const pidRedux = useSelector((state) => state.user.projectId);

  // =========================
  // Filter Options
  // =========================
  const [orgOptions, setOrgOptions] = useState([]);
  const [schemeOptions, setSchemeOptions] = useState([]);
  const [schemeTypeOptions, setSchemeTypeOptions] = useState([]);
  const [hdidOptions, setHdidOptions] = useState([]);
  const [hdNameOptions, setHdNameOptions] = useState([]);

  // =========================
  // Selected Filters (LIVE UI)
  // =========================
  const [selectedOrgs, setSelectedOrgs] = useState([]);
  const [selectedSchemes, setSelectedSchemes] = useState([]);
  const [selectedSchemeTypes, setSelectedSchemeTypes] = useState([]);
  const [selectedHDID, setSelectedHDID] = useState([]);
  const [selectedHDNames, setSelectedHDNames] = useState([]);

  // Date Filters
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [dateError, setDateError] = useState("");

  // Applied Filters (KPI only)
  const [appliedFilters, setAppliedFilters] = useState({
    orgs: [],
    schemes: [],
    schemeTypes: [],
    hdid: [],
    hdnames: [],
    startDate: "",
    endDate: "",
  });

  // KPI Data
  const [metrics, setMetrics] = useState({
    open: 0,
    ds: 0,
    br: 0,
    aborted: 0,
  });

  const [loading, setLoading] = useState(false);

  const BASE = "https://api-project360-insights.haqdarshak.com/kpi/hd";

  // =========================
  // Helper: Replace options but keep selected values visible
  // =========================
  const buildOptions = (fetchedValues, selectedValues) => {
    const fetched = fetchedValues.map((v) => ({ value: v, label: v }));
    const selectedOnly = selectedValues
      .filter((v) => !fetchedValues.includes(v))
      .map((v) => ({ value: v, label: v }));
    return [...selectedOnly, ...fetched];
  };

  // =========================
  // Query for DEPENDENT FILTERS (LIVE UI)
  // =========================
  const getFilterQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    selectedOrgs.forEach((o) => params.append("OrgName", o));
    selectedSchemes.forEach((s) => params.append("SchemeName", s));
    selectedSchemeTypes.forEach((t) => params.append("SchemeType", t));
    selectedHDID.forEach((h) => params.append("HDID", h));
    selectedHDNames.forEach((n) => params.append("haqdarshakName", n));
    if (pidRedux) params.append("pid", pidRedux);
    return params.toString();
  }, [
    selectedOrgs,
    selectedSchemes,
    selectedSchemeTypes,
    selectedHDID,
    selectedHDNames,
    pidRedux,
  ]);

  // =========================
  // Query for KPI (APPLIED FILTERS)
  // =========================
  const getKPIQueryParams = useCallback(() => {
    const params = new URLSearchParams();
    appliedFilters.orgs.forEach((o) => params.append("OrgName", o));
    appliedFilters.schemes.forEach((s) => params.append("SchemeName", s));
    appliedFilters.schemeTypes.forEach((t) => params.append("SchemeType", t));
    appliedFilters.hdid.forEach((h) => params.append("HDID", h));
    appliedFilters.hdnames.forEach((n) => params.append("haqdarshakName", n));
    if (pidRedux) params.append("pid", pidRedux);
    if (appliedFilters.startDate) params.append("start_date", appliedFilters.startDate);
    if (appliedFilters.endDate) params.append("end_date", appliedFilters.endDate);
    return params.toString();
  }, [appliedFilters, pidRedux]);

  // =========================
  // Fetch Dependent Filters (LIVE)
  // =========================
  const fetchFilterOptions = useCallback(async () => {
    try {
      const params = getFilterQueryParams();

      const [orgRes, schemeRes, typeRes, hdidRes, hdnameRes] =
        await Promise.all([
          fetch(`${BASE}/unique-org-name/?${params}`),
          fetch(`${BASE}/unique-scheme/?${params}`),
          fetch(`${BASE}/unique-type/?${params}`),
          fetch(`${BASE}/unique-hd-id/?${params}`),
          fetch(`${BASE}/unique-hd-name/?${params}`),
        ]);

      const orgData = await orgRes.json();
      const schemeData = await schemeRes.json();
      const typeData = await typeRes.json();
      const hdidData = await hdidRes.json();
      const hdnameData = await hdnameRes.json();

      setOrgOptions(buildOptions(orgData.unique_Organizations ?? [], selectedOrgs));
      setSchemeOptions(buildOptions(schemeData.unique_Schemes ?? [], selectedSchemes));
      setSchemeTypeOptions(buildOptions(typeData.unique_Scheme_Types ?? [], selectedSchemeTypes));
      setHdidOptions(buildOptions(hdidData.unique_HDID ?? [], selectedHDID));
      setHdNameOptions(buildOptions(hdnameData.unique_HaqdarshakNames ?? [], selectedHDNames));
    } catch (e) {
      console.error("Filter fetch error", e);
    }
  }, [
    getFilterQueryParams,
    selectedOrgs,
    selectedSchemes,
    selectedSchemeTypes,
    selectedHDID,
    selectedHDNames,
  ]);

  // =========================
  // Fetch KPI Totals
  // ✅ Uses agg-with-date if both dates applied, else agg-combined
  // =========================
  const fetchKPIs = useCallback(async () => {
    setLoading(true);
    try {
      const params = getKPIQueryParams();
      const endpoint =
        appliedFilters.startDate && appliedFilters.endDate
          ? `${BASE}/agg-with-date/?${params}`
          : `${BASE}/agg-combined/?${params}`;

      const res = await fetch(endpoint);
      const data = await res.json();

      setMetrics({
        open: data.Total_Open_Count || 0,
        ds: data.Total_DS_Count || 0,
        br: data.Total_BR_Count || 0,
        aborted: data.Total_Aborted_Count || 0,
      });
    } catch (e) {
      console.error("KPI fetch error", e);
    } finally {
      setLoading(false);
    }
  }, [getKPIQueryParams, appliedFilters.startDate, appliedFilters.endDate]);

  // =========================
  // LIVE Dependent Filters (auto refresh)
  // =========================
  useEffect(() => {
    const t = setTimeout(() => {
      fetchFilterOptions();
    }, 200);
    return () => clearTimeout(t);
  }, [
    selectedOrgs,
    selectedSchemes,
    selectedSchemeTypes,
    selectedHDID,
    selectedHDNames,
    pidRedux,
  ]);

  // KPI refresh only on Apply
  useEffect(() => {
    fetchKPIs();
  }, [appliedFilters, pidRedux]);

  // =========================
  // Date change handlers with live validation
  // =========================
  const handleStartDateChange = (e) => {
    const val = e.target.value;
    setStartDate(val);
    if (val && !endDate) {
      setDateError("Please also select an End Date.");
    } else if (!val && endDate) {
      setDateError("Please also select a Start Date.");
    } else {
      setDateError("");
    }
  };

  const handleEndDateChange = (e) => {
    const val = e.target.value;
    setEndDate(val);
    if (val && !startDate) {
      setDateError("Please also select a Start Date.");
    } else if (!val && startDate) {
      setDateError("Please also select an End Date.");
    } else {
      setDateError("");
    }
  };

  // =========================
  // Apply / Clear
  // =========================
  const applyFilters = () => {
    // ✅ Validation: one date without the other blocks Apply
    if (startDate && !endDate) {
      setDateError("End Date is required when Start Date is selected.");
      return;
    }
    if (!startDate && endDate) {
      setDateError("Start Date is required when End Date is selected.");
      return;
    }
    setDateError("");
    setAppliedFilters({
      orgs: selectedOrgs,
      schemes: selectedSchemes,
      schemeTypes: selectedSchemeTypes,
      hdid: selectedHDID,
      hdnames: selectedHDNames,
      startDate,
      endDate,
    });
  };

  const clearFilters = () => {
    setSelectedOrgs([]);
    setSelectedSchemes([]);
    setSelectedSchemeTypes([]);
    setSelectedHDID([]);
    setSelectedHDNames([]);
    setStartDate("");
    setEndDate("");
    setDateError("");
    setAppliedFilters({
      orgs: [],
      schemes: [],
      schemeTypes: [],
      hdid: [],
      hdnames: [],
      startDate: "",
      endDate: "",
    });
  };

  const MultiSelect = ({ options, selected, setSelected, placeholder }) => (
    <Select
      isMulti
      options={options}
      value={options.filter((o) => selected.includes(o.value))}
      onChange={(opts) => setSelected(opts ? opts.map((o) => o.value) : [])}
      placeholder={placeholder}
      closeMenuOnSelect={false}
    />
  );

  return (
    <div className="max-w-[92vw] mt-[3vh] border shadow rounded bg-white">
      <div className="px-6 py-4 border-b">
        <h2 className="text-xl font-semibold text-pink-800">
          HD Performance Dashboard
        </h2>
      </div>

      {/* Filters */}
      <div className="px-6 py-4 grid md:grid-cols-3 gap-4">
        <MultiSelect
          options={orgOptions}
          selected={selectedOrgs}
          setSelected={setSelectedOrgs}
          placeholder="Organization"
        />
        <MultiSelect
          options={schemeOptions}
          selected={selectedSchemes}
          setSelected={setSelectedSchemes}
          placeholder="Scheme Name"
        />
        <MultiSelect
          options={schemeTypeOptions}
          selected={selectedSchemeTypes}
          setSelected={setSelectedSchemeTypes}
          placeholder="Service Type"
        />
        <MultiSelect
          options={hdidOptions}
          selected={selectedHDID}
          setSelected={setSelectedHDID}
          placeholder="HD ID"
        />
        <MultiSelect
          options={hdNameOptions}
          selected={selectedHDNames}
          setSelected={setSelectedHDNames}
          placeholder="Haqdarshak Name"
        />f

        {/* Start Date */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">Start Date</label>
          <input
            type="date"
            value={startDate}
            onChange={handleStartDateChange}
            className={`border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
              dateError && !startDate ? "border-red-500" : "border-gray-300"
            }`}
          />
        </div>

        {/* End Date */}
        <div className="flex flex-col gap-1">
          <label className="text-xs text-gray-500 font-medium">End Date</label>
          <input
            type="date"
            value={endDate}
            onChange={handleEndDateChange}
            min={startDate || undefined}
            className={`border rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400 ${
              dateError && !endDate ? "border-red-500" : "border-gray-300"
            }`}
          />
        </div>

        {/* Validation + Buttons — full width row */}
        <div className="md:col-span-3 flex flex-col gap-2">
          {dateError && (
            <p className="text-red-500 text-xs font-medium">⚠ {dateError}</p>
          )}
          <div className="flex gap-3">
            <button
              onClick={clearFilters}
              className="px-4 py-2 bg-gray-200 rounded hover:bg-gray-300 transition disabled:opacity-50"
            >
              Clear Filters
            </button>
            <button
              onClick={applyFilters}
              className="px-4 py-2 bg-blue-700 text-white rounded"
            >
              Apply Filters
            </button>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-4 bg-gray-50">
        {loading ? (
          <div>Loading...</div>
        ) : (
          <>
            <Card title="Application Open" value={metrics.open} />
            <Card title="Docket Submitted" value={metrics.ds} />
            <Card title="BR Count" value={metrics.br} />
            <Card title="Aborted Cases" value={metrics.aborted} />
          </>
        )}
      </div>
    </div>
  );
};

const Card = ({ title, value }) => (
  <div className="bg-white p-4 rounded shadow border">
    <p className="text-xs text-gray-500">{title}</p>
    <p className="text-2xl font-bold text-indigo-700">
      {value?.toLocaleString()}
    </p>
  </div>
);

export default HDPerformanceDashboard;